import { Box, FormHelperText, Typography } from "@mui/material";
import { grey } from "@mui/material/colors";
import { useState } from "react";
import { CustomEvent } from "schemas/functions";

import StyledLargeButton from "components/Styled/Button/StyledLargeButton";
import StyledTextField from "components/Styled/TextField";

interface Props {
  handleNext: (portfolioLink: string, additionalInfo: string) => void;
  handleBack: () => void;
}

const URL_REGEX = /^(https?:\/\/)?.+\..+/;

const PortfolioStep = ({
  portfolioLink,
  setPortfolioLink,
  onNext,
  onBack,
}: {
  portfolioLink: string;
  setPortfolioLink: (v: string) => void;
  onNext: () => void;
  onBack: () => void;
}) => {
  const hasInput = portfolioLink.trim().length > 0;
  const isUrlValid = URL_REGEX.test(portfolioLink);
  const showUrlError = hasInput && !isUrlValid;
  const canProceed = !hasInput || isUrlValid;

  return (
    <Box>
      <Typography variant="h4" gutterBottom>
        What is your Portfolio? 👀
      </Typography>

      <Box sx={{ mb: 3 }}>
        <StyledTextField
          fullWidth
          label="Portfolio URL"
          placeholder="www.yoursite.com"
          value={portfolioLink}
          onChange={(e: CustomEvent) => setPortfolioLink(e.target.value)}
          error={showUrlError}
          helperText={
            showUrlError
              ? "Please enter a valid URL (e.g. https://yoursite.com)"
              : ""
          }
        />

        <FormHelperText sx={{ color: grey[700], pl: 2 }}>
          <Box
            component="i"
            className="fa-regular fa-lightbulb"
            sx={{ fontSize: 14, color: "primary.main" }}
          />{" "}
          <Typography variant="body2" fontWeight={600} component={"span"}>
            Tip
          </Typography>{" "}
          — Providing a portfolio link increases reply rate
        </FormHelperText>
      </Box>

      <Box sx={{ display: "flex", justifyContent: "space-between", mt: 4 }}>
        <StyledLargeButton color="secondary" onClick={onBack}>
          Back
        </StyledLargeButton>
        <StyledLargeButton
          variant="contained"
          disableElevation
          disabled={!canProceed}
          onClick={onNext}
        >
          Next
        </StyledLargeButton>
      </Box>
    </Box>
  );
};

const AdditionalInfoStep = ({
  additionalInfo,
  setAdditionalInfo,
  isRequired,
  onNext,
  onBack,
}: {
  additionalInfo: string;
  setAdditionalInfo: (v: string) => void;
  isRequired: boolean;
  onNext: () => void;
  onBack: () => void;
}) => {
  const canProceed = !isRequired || additionalInfo.trim().length > 0;

  return (
    <Box>
      <Typography variant="h4" gutterBottom>
        Any additional info to share? 📝
      </Typography>

      <Box sx={{ my: 1 }}>
        <StyledTextField
          fullWidth
          multiline
          rows={4}
          label={``}
          placeholder="I'm Maya, a Miami-based lifestyle and beauty creator. I can speak two languages and have been doing UGC for 10 years..."
          value={additionalInfo}
          onChange={(e: CustomEvent) => setAdditionalInfo(e.target.value)}
          sx={{ mb: 2 }}
        />
        <FormHelperText sx={{ color: grey[700], pl: 2 }}>
          <Box
            component="i"
            className="fa-regular fa-lightbulb"
            sx={{ fontSize: 14, color: "primary.main" }}
          />{" "}
          <Typography variant="body2" fontWeight={600} component={"span"}>
            Tip
          </Typography>{" "}
          — for best results, be specific.
        </FormHelperText>
      </Box>

      <Box sx={{ display: "flex", justifyContent: "space-between", mt: 4 }}>
        <StyledLargeButton color="secondary" onClick={onBack}>
          Back
        </StyledLargeButton>
        <StyledLargeButton
          variant="contained"
          disableElevation
          disabled={!canProceed}
          onClick={onNext}
        >
          Next
        </StyledLargeButton>
      </Box>
    </Box>
  );
};

const PortfolioLinkForm = ({ handleNext, handleBack }: Props) => {
  const [step, setStep] = useState<1 | 2>(1);
  const [portfolioLink, setPortfolioLink] = useState("");
  const [additionalInfo, setAdditionalInfo] = useState("");

  const hasPortfolio = URL_REGEX.test(portfolioLink);

  if (step === 1) {
    return (
      <PortfolioStep
        portfolioLink={portfolioLink}
        setPortfolioLink={setPortfolioLink}
        onNext={() => setStep(2)}
        onBack={handleBack}
      />
    );
  }

  return (
    <AdditionalInfoStep
      additionalInfo={additionalInfo}
      setAdditionalInfo={setAdditionalInfo}
      isRequired={!hasPortfolio}
      onNext={() => handleNext(portfolioLink, additionalInfo)}
      onBack={() => setStep(1)}
    />
  );
};

export default PortfolioLinkForm;
